import { supabase } from '@/integrations/supabase/client';

export interface AuditLogEntry {
  action: string;
  resourceType: string;
  resourceId?: string;
  details?: Record<string, any>;
}

export class AuditLogger {
  static async log(entry: AuditLogEntry) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.warn('No authenticated user for audit log');
        return;
      }

      // Get client IP and user agent (best effort)
      const details = {
        ...entry.details,
        timestamp: new Date().toISOString(),
        userEmail: user.email
      };

      const { error } = await supabase
        .from('admin_audit_logs')
        .insert([{
          user_id: user.id,
          action: entry.action,
          resource_type: entry.resourceType,
          resource_id: entry.resourceId || null,
          details: details,
          ip_address: 'unknown', // Client-side can't reliably get IP
          user_agent: navigator.userAgent
        }]);

      if (error) {
        console.error('Failed to create audit log:', error);
      } else {
        console.log('Audit log created:', entry.action);
      }
    } catch (error) {
      console.error('Error creating audit log:', error);
    }
  }

  // Convenience methods for common actions
  static async logArticleCreated(articleId: string, articleTitle: string) {
    await this.log({
      action: 'ARTICLE_CREATED',
      resourceType: 'ARTICLE',
      resourceId: articleId,
      details: { title: articleTitle }
    });
  }

  static async logArticleUpdated(articleId: string, articleTitle: string, changes: Record<string, any>) {
    await this.log({
      action: 'ARTICLE_UPDATED',
      resourceType: 'ARTICLE', 
      resourceId: articleId,
      details: { title: articleTitle, changes }
    });
  }

  static async logArticleDeleted(articleId: string, articleTitle: string) {
    await this.log({
      action: 'ARTICLE_DELETED',
      resourceType: 'ARTICLE',
      resourceId: articleId,
      details: { title: articleTitle }
    });
  }

  static async logArticleFeatured(articleId: string, articleTitle: string, featured: boolean) {
    await this.log({
      action: featured ? 'ARTICLE_FEATURED' : 'ARTICLE_UNFEATURED',
      resourceType: 'ARTICLE',
      resourceId: articleId,
      details: { title: articleTitle, featured }
    });
  }

  static async logImageUploaded(filename: string, size: number) {
    await this.log({
      action: 'IMAGE_UPLOADED',
      resourceType: 'MEDIA',
      details: { filename, sizeBytes: size }
    });
  }
}